import { LEVELS, seededDailyLevel, type LevelDefinition } from './levels';
import type { GameCallbacks, GameMode, GameSnapshot, GameStatus, UpgradeId, UpgradeOption } from './types';
import { getHighScore, saveScore, setHighScore } from '../utils/storage';

const WIDTH = 960;
const HEIGHT = 600;

type PowerType = 'multi' | 'wide' | 'slow';
type BrickKind = 'normal' | 'armored' | 'explosive' | 'bonus';

interface Ball {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  stuck: boolean;
  damage: number;
  trail: { x: number; y: number; alpha: number }[];
}

interface Brick {
  x: number;
  y: number;
  w: number;
  h: number;
  hp: number;
  maxHp: number;
  kind: BrickKind;
  alive: boolean;
  flash: number;
}

interface Paddle {
  x: number;
  y: number;
  w: number;
  h: number;
  targetX: number;
}

interface PowerUp {
  x: number;
  y: number;
  vy: number;
  type: PowerType;
  rotation: number;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: string;
}

const UPGRADE_OPTIONS: UpgradeOption[] = [
  { id: 'power', name: '能量穿透', description: '弹球伤害提高，装甲砖更容易被击破。', emoji: '⚡' },
  { id: 'wide', name: '扩展挡板', description: '永久增加挡板宽度，提高接球容错。', emoji: '↔️' },
  { id: 'multiball', name: '复制核心', description: '下一关开局额外生成一颗弹球。', emoji: '🔮' }
];

export class BrickBreakerEngine {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly callbacks: GameCallbacks;
  private readonly mode: GameMode;
  private animationId = 0;
  private lastTime = 0;
  private accumulator = 0;
  private status: GameStatus = 'idle';
  private score = 0;
  private levelIndex = 0;
  private lives = 3;
  private combo = 0;
  private highScore = getHighScore();
  private balls: Ball[] = [];
  private bricks: Brick[] = [];
  private powers: PowerUp[] = [];
  private particles: Particle[] = [];
  private paddle: Paddle = { x: WIDTH / 2 - 72, y: HEIGHT - 48, w: 144, h: 17, targetX: WIDTH / 2 };
  private keys = new Set<string>();
  private soundEnabled = true;
  private audioContext: AudioContext | null = null;
  private levelDef: LevelDefinition = LEVELS[0];
  private powerLevel = 0;
  private paddleLevel = 0;
  private extraStartBalls = 0;
  private wideUntil = 0;
  private slowUntil = 0;
  private lastSnapshot = '';
  private dailySeed = 0;
  private destroyed = false;

  constructor(canvas: HTMLCanvasElement, mode: GameMode, callbacks: GameCallbacks) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context unavailable');
    this.ctx = ctx;
    this.mode = mode;
    this.callbacks = callbacks;
    this.resizeCanvas();
    this.bindEvents();
    this.newGame();
    this.animationId = requestAnimationFrame(this.frame);
  }

  private resizeCanvas = () => {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    this.canvas.width = Math.floor(WIDTH * dpr);
    this.canvas.height = Math.floor(HEIGHT * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };

  private bindEvents(): void {
    window.addEventListener('resize', this.resizeCanvas);
    window.addEventListener('keydown', this.onKeyDown, { passive: false });
    window.addEventListener('keyup', this.onKeyUp);
    this.canvas.addEventListener('pointermove', this.onPointerMove);
    this.canvas.addEventListener('pointerdown', this.onPointerDown);
    this.canvas.addEventListener('contextmenu', this.preventContextMenu);
  }

  private preventContextMenu = (event: Event) => event.preventDefault();

  private onPointerMove = (event: PointerEvent) => {
    const rect = this.canvas.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * WIDTH;
    this.paddle.targetX = x;
  };

  private onPointerDown = (event: PointerEvent) => {
    event.preventDefault();
    this.ensureAudio();
    if (this.status === 'ready') this.launch();
  };

  private onKeyDown = (event: KeyboardEvent) => {
    const key = event.key.toLowerCase();
    if (['arrowleft', 'arrowright', 'a', 'd', ' ', 'p', 'r'].includes(key)) event.preventDefault();
    this.keys.add(key);
    if (key === ' ') {
      this.ensureAudio();
      if (this.status === 'ready') this.launch();
    }
    if (key === 'p') this.togglePause();
    if (key === 'r') this.restart();
  };

  private onKeyUp = (event: KeyboardEvent) => {
    this.keys.delete(event.key.toLowerCase());
  };

  newGame(): void {
    this.score = 0;
    this.levelIndex = 0;
    this.lives = 3;
    this.combo = 0;
    this.powerLevel = 0;
    this.paddleLevel = 0;
    this.extraStartBalls = 0;
    this.dailySeed = Number(new Date().toISOString().slice(0, 10).replaceAll('-', ''));
    this.loadLevel(0);
  }

  restart(): void {
    this.newGame();
    this.callbacks.onToast('已重新开始本局');
  }

  private loadLevel(index: number): void {
    this.levelIndex = index;
    this.levelDef = this.mode === 'daily' ? seededDailyLevel(this.dailySeed) : LEVELS[index];
    this.powers = [];
    this.particles = [];
    this.combo = 0;
    this.paddle.w = 144 + this.paddleLevel * 22;
    this.paddle.x = WIDTH / 2 - this.paddle.w / 2;
    this.paddle.targetX = WIDTH / 2;
    this.buildBricks();
    this.resetBalls(true);
    this.status = 'ready';
    this.emitSnapshot(true);
  }

  private buildBricks(): void {
    const marginX = 42;
    const gap = 8;
    const top = 72;
    const totalGap = gap * (this.levelDef.cols - 1);
    const brickW = (WIDTH - marginX * 2 - totalGap) / this.levelDef.cols;
    const brickH = 30;
    this.bricks = this.levelDef.bricks.map((item) => ({
      x: marginX + item.col * (brickW + gap),
      y: top + item.row * (brickH + gap),
      w: brickW,
      h: brickH,
      hp: item.hp,
      maxHp: item.hp,
      kind: item.kind,
      alive: true,
      flash: 0
    }));
  }

  private resetBalls(includeExtras = false): void {
    const baseSpeed = this.levelDef.speed;
    const damage = 1 + this.powerLevel;
    this.balls = [this.createBall(true, baseSpeed, damage)];
    if (includeExtras && this.extraStartBalls > 0) {
      for (let i = 0; i < this.extraStartBalls; i++) {
        const ball = this.createBall(true, baseSpeed, damage);
        ball.x += (i + 1) * 16;
        this.balls.push(ball);
      }
      this.extraStartBalls = 0;
    }
  }

  private createBall(stuck: boolean, speed: number, damage: number): Ball {
    const angle = (-Math.PI / 2) + (Math.random() - 0.5) * 0.55;
    return {
      x: this.paddle.x + this.paddle.w / 2,
      y: this.paddle.y - 12,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      radius: 8,
      stuck,
      damage,
      trail: []
    };
  }

  launch(): void {
    if (this.status !== 'ready') return;
    this.status = 'running';
    for (const ball of this.balls) ball.stuck = false;
    this.beep(470, 0.05, 0.05);
    this.emitSnapshot(true);
  }

  togglePause(): void {
    if (this.status === 'running') this.status = 'paused';
    else if (this.status === 'paused') this.status = 'running';
    this.emitSnapshot(true);
  }

  setPaused(paused: boolean): void {
    if (paused && this.status === 'running') this.status = 'paused';
    if (!paused && this.status === 'paused') this.status = 'running';
    this.emitSnapshot(true);
  }

  toggleSound(): boolean {
    this.soundEnabled = !this.soundEnabled;
    if (this.soundEnabled) this.ensureAudio();
    return this.soundEnabled;
  }

  applyUpgrade(id: UpgradeId): void {
    if (id === 'power') this.powerLevel += 1;
    if (id === 'wide') this.paddleLevel += 1;
    if (id === 'multiball') this.extraStartBalls += 1;
    const next = this.levelIndex + 1;
    if (this.mode === 'daily' || next >= LEVELS.length) {
      this.status = 'victory';
      this.finishScore('胜利');
      this.callbacks.onVictory(this.snapshot());
      return;
    }
    this.loadLevel(next);
    this.callbacks.onToast(`强化完成：${UPGRADE_OPTIONS.find((item) => item.id === id)?.name ?? ''}`);
  }

  private frame = (time: number) => {
    if (this.destroyed) return;
    const delta = Math.min(0.033, Math.max(0, (time - this.lastTime) / 1000 || 0));
    this.lastTime = time;
    this.accumulator += delta;
    const step = 1 / 120;
    while (this.accumulator >= step) {
      if (this.status === 'running') this.update(step, time);
      else this.updateIdle(step, time);
      this.accumulator -= step;
    }
    this.draw(time);
    this.animationId = requestAnimationFrame(this.frame);
  };

  private updateIdle(dt: number, time: number): void {
    this.updatePaddle(dt);
    if (this.status === 'ready') {
      this.balls.forEach((ball, i) => {
        ball.x = this.paddle.x + this.paddle.w / 2 + i * 18;
        ball.y = this.paddle.y - ball.radius - 2;
      });
    }
    this.updateParticles(dt);
    this.expireEffects(time);
  }

  private update(dt: number, time: number): void {
    this.updatePaddle(dt);
    this.expireEffects(time);
    for (const brick of this.bricks) brick.flash = Math.max(0, brick.flash - dt * 4);
    for (const ball of this.balls) this.updateBall(ball, dt);
    this.balls = this.balls.filter((ball) => ball.y < HEIGHT + 45);
    if (this.balls.length === 0) this.loseLife();
    this.updatePowerUps(dt);
    this.updateParticles(dt);
    this.emitSnapshot();
  }

  private updatePaddle(dt: number): void {
    const keyboardSpeed = 650;
    if (this.keys.has('arrowleft') || this.keys.has('a')) this.paddle.targetX -= keyboardSpeed * dt;
    if (this.keys.has('arrowright') || this.keys.has('d')) this.paddle.targetX += keyboardSpeed * dt;
    this.paddle.targetX = Math.max(this.paddle.w / 2, Math.min(WIDTH - this.paddle.w / 2, this.paddle.targetX));
    const desiredX = this.paddle.targetX - this.paddle.w / 2;
    this.paddle.x += (desiredX - this.paddle.x) * Math.min(1, dt * 16);
    this.paddle.x = Math.max(0, Math.min(WIDTH - this.paddle.w, this.paddle.x));
  }

  private expireEffects(time: number): void {
    const baseWidth = 144 + this.paddleLevel * 22;
    this.paddle.w = time < this.wideUntil ? baseWidth * 1.55 : baseWidth;
    if (time >= this.slowUntil && this.slowUntil !== 0) this.slowUntil = 0;
  }

  private updateBall(ball: Ball, dt: number): void {
    if (ball.stuck) return;
    ball.trail.unshift({ x: ball.x, y: ball.y, alpha: 0.55 });
    if (ball.trail.length > 9) ball.trail.pop();
    ball.trail.forEach((point) => point.alpha *= 0.87);

    const multiplier = this.slowUntil > performance.now() ? 0.78 : 1;
    ball.x += ball.vx * dt * multiplier;
    ball.y += ball.vy * dt * multiplier;

    if (ball.x - ball.radius <= 0) { ball.x = ball.radius; ball.vx = Math.abs(ball.vx); this.wallSound(); }
    if (ball.x + ball.radius >= WIDTH) { ball.x = WIDTH - ball.radius; ball.vx = -Math.abs(ball.vx); this.wallSound(); }
    if (ball.y - ball.radius <= 0) { ball.y = ball.radius; ball.vy = Math.abs(ball.vy); this.wallSound(); }

    if (ball.vy > 0 && this.circleRect(ball, this.paddle.x, this.paddle.y, this.paddle.w, this.paddle.h)) {
      ball.y = this.paddle.y - ball.radius - 1;
      const relative = (ball.x - (this.paddle.x + this.paddle.w / 2)) / (this.paddle.w / 2);
      const speed = Math.min(610, Math.max(this.levelDef.speed, Math.hypot(ball.vx, ball.vy) * 1.015));
      const angle = relative * 1.05;
      ball.vx = Math.sin(angle) * speed;
      ball.vy = -Math.cos(angle) * speed;
      this.combo = 0;
      this.beep(250, 0.028, 0.03);
      this.spawnParticles(ball.x, ball.y, '#47e6ff', 7, 105);
    }

    for (const brick of this.bricks) {
      if (!brick.alive || !this.circleRect(ball, brick.x, brick.y, brick.w, brick.h)) continue;
      this.resolveBrickCollision(ball, brick);
      this.damageBrick(brick, ball.damage);
      break;
    }
  }

  private resolveBrickCollision(ball: Ball, brick: Brick): void {
    const closestX = Math.max(brick.x, Math.min(ball.x, brick.x + brick.w));
    const closestY = Math.max(brick.y, Math.min(ball.y, brick.y + brick.h));
    const dx = ball.x - closestX;
    const dy = ball.y - closestY;
    if (Math.abs(dx) > Math.abs(dy)) ball.vx *= -1;
    else ball.vy *= -1;
  }

  private damageBrick(brick: Brick, damage: number, fromExplosion = false): void {
    if (!brick.alive) return;
    brick.hp -= damage;
    brick.flash = 1;
    this.combo += 1;
    const comboBonus = Math.min(10, this.combo);
    this.score += 10 * comboBonus;
    if (this.score > this.highScore) {
      this.highScore = this.score;
      setHighScore(this.score);
    }
    this.beep(brick.kind === 'armored' ? 310 : 390 + Math.min(250, this.combo * 9), 0.025, 0.025);
    this.spawnParticles(brick.x + brick.w / 2, brick.y + brick.h / 2, this.brickColor(brick), 6, 90);

    if (brick.hp <= 0) {
      brick.alive = false;
      this.score += brick.kind === 'armored' ? 80 : brick.kind === 'explosive' ? 110 : brick.kind === 'bonus' ? 130 : 45;
      this.spawnParticles(brick.x + brick.w / 2, brick.y + brick.h / 2, this.brickColor(brick), 14, 170);
      if (brick.kind === 'explosive' && !fromExplosion) this.explodeBrick(brick);
      if (brick.kind === 'bonus' || Math.random() < 0.055) this.dropPowerUp(brick);
      if (this.bricks.every((item) => !item.alive)) this.levelCleared();
    }
  }

  private explodeBrick(source: Brick): void {
    const sx = source.x + source.w / 2;
    const sy = source.y + source.h / 2;
    this.beep(105, 0.13, 0.08);
    this.spawnParticles(sx, sy, '#ff8a00', 34, 260);
    for (const brick of this.bricks) {
      if (!brick.alive || brick === source) continue;
      const bx = brick.x + brick.w / 2;
      const by = brick.y + brick.h / 2;
      if (Math.hypot(bx - sx, by - sy) < 110) this.damageBrick(brick, 1 + this.powerLevel, true);
    }
  }

  private dropPowerUp(brick: Brick): void {
    const types: PowerType[] = ['multi', 'wide', 'slow'];
    this.powers.push({
      x: brick.x + brick.w / 2,
      y: brick.y + brick.h / 2,
      vy: 135,
      type: types[Math.floor(Math.random() * types.length)],
      rotation: 0
    });
  }

  private updatePowerUps(dt: number): void {
    for (const power of this.powers) {
      power.y += power.vy * dt;
      power.rotation += dt * 2.4;
      if (this.circleRect({ x: power.x, y: power.y, radius: 12 } as Ball, this.paddle.x, this.paddle.y, this.paddle.w, this.paddle.h)) {
        power.y = HEIGHT + 100;
        this.applyPower(power.type);
      }
    }
    this.powers = this.powers.filter((power) => power.y < HEIGHT + 30);
  }

  private applyPower(type: PowerType): void {
    if (type === 'multi') {
      const originals = [...this.balls];
      for (const ball of originals) {
        if (this.balls.length >= 7) break;
        this.balls.push({ ...ball, vx: -ball.vx * 0.92, vy: ball.vy, trail: [] });
      }
      this.callbacks.onToast('多重弹球：弹球数量翻倍');
    }
    if (type === 'wide') {
      this.wideUntil = performance.now() + 12000;
      this.callbacks.onToast('磁力挡板：宽度提升 12 秒');
    }
    if (type === 'slow') {
      this.slowUntil = performance.now() + 9000;
      this.callbacks.onToast('时间减速：弹球减速 9 秒');
    }
    this.beep(720, 0.1, 0.06);
  }

  private loseLife(): void {
    this.lives -= 1;
    this.combo = 0;
    this.beep(120, 0.25, 0.08);
    if (this.lives <= 0) {
      this.status = 'game-over';
      this.finishScore('普通');
      const snapshot = this.snapshot();
      this.emitSnapshot(true);
      this.callbacks.onGameOver(snapshot);
      return;
    }
    this.resetBalls(false);
    this.status = 'ready';
    this.callbacks.onToast(`失去一颗核心，还剩 ${this.lives} 次机会`);
    this.emitSnapshot(true);
  }

  private levelCleared(): void {
    if (this.status !== 'running') return;
    this.score += 1000 * (this.levelIndex + 1);
    this.beep(820, 0.16, 0.07);
    const isFinalLevel = this.mode === 'daily' || this.levelIndex + 1 >= LEVELS.length;
    if (isFinalLevel) {
      this.status = 'victory';
      this.finishScore('胜利');
      const snapshot = this.snapshot();
      this.emitSnapshot(true);
      this.callbacks.onVictory(snapshot);
      return;
    }
    this.status = 'level-clear';
    this.emitSnapshot(true);
    this.callbacks.onLevelClear(UPGRADE_OPTIONS);
  }

  private finishScore(label: string): void {
    saveScore(this.score, this.mode === 'daily' ? '每日挑战' : label);
    this.highScore = Math.max(this.highScore, this.score);
  }

  private circleRect(ball: Pick<Ball, 'x' | 'y' | 'radius'>, x: number, y: number, w: number, h: number): boolean {
    const closestX = Math.max(x, Math.min(ball.x, x + w));
    const closestY = Math.max(y, Math.min(ball.y, y + h));
    const dx = ball.x - closestX;
    const dy = ball.y - closestY;
    return dx * dx + dy * dy <= ball.radius * ball.radius;
  }

  private updateParticles(dt: number): void {
    for (const p of this.particles) {
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vy += 190 * dt;
      p.life -= dt;
    }
    this.particles = this.particles.filter((p) => p.life > 0);
  }

  private spawnParticles(x: number, y: number, color: string, count: number, speed: number): void {
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const magnitude = speed * (0.35 + Math.random() * 0.75);
      const life = 0.28 + Math.random() * 0.44;
      this.particles.push({ x, y, vx: Math.cos(angle) * magnitude, vy: Math.sin(angle) * magnitude, life, maxLife: life, size: 2 + Math.random() * 5, color });
    }
  }

  private draw(time: number): void {
    const ctx = this.ctx;
    ctx.clearRect(0, 0, WIDTH, HEIGHT);
    this.drawBackground(time);
    this.drawBricks();
    this.drawPowerUps();
    this.drawPaddle();
    this.drawBalls();
    this.drawParticles();
    this.drawCanvasHud();
    if (this.status === 'ready') this.drawReadyMessage();
  }

  private drawBackground(time: number): void {
    const ctx = this.ctx;
    const gradient = ctx.createLinearGradient(0, 0, 0, HEIGHT);
    gradient.addColorStop(0, '#05071c');
    gradient.addColorStop(0.52, '#071a44');
    gradient.addColorStop(1, '#030616');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, WIDTH, HEIGHT);

    ctx.save();
    ctx.globalAlpha = 0.32;
    ctx.strokeStyle = '#1d76bd';
    ctx.lineWidth = 1;
    const offset = (time * 0.02) % 42;
    for (let y = HEIGHT * 0.53; y < HEIGHT; y += 42) {
      ctx.beginPath(); ctx.moveTo(0, y + offset); ctx.lineTo(WIDTH, y + offset); ctx.stroke();
    }
    for (let x = -WIDTH; x < WIDTH * 2; x += 64) {
      ctx.beginPath(); ctx.moveTo(WIDTH / 2, HEIGHT * 0.48); ctx.lineTo(x, HEIGHT); ctx.stroke();
    }
    ctx.restore();

    ctx.save();
    for (let i = 0; i < 42; i++) {
      const x = (i * 173) % WIDTH;
      const y = 18 + ((i * 71) % 270);
      const pulse = 0.35 + Math.sin(time * 0.002 + i) * 0.25;
      ctx.fillStyle = `rgba(116,220,255,${pulse})`;
      ctx.fillRect(x, y, i % 5 === 0 ? 2 : 1, i % 5 === 0 ? 2 : 1);
    }
    ctx.restore();

    ctx.save();
    ctx.globalAlpha = 0.5;
    for (let i = 0; i < 17; i++) {
      const x = i * 62 - 18;
      const h = 35 + ((i * 47) % 94);
      ctx.fillStyle = i % 3 === 0 ? '#0c2955' : '#091d40';
      ctx.fillRect(x, HEIGHT * 0.5 - h, 43, h);
      ctx.fillStyle = 'rgba(44,210,255,.35)';
      for (let wy = HEIGHT * 0.5 - h + 9; wy < HEIGHT * 0.5 - 7; wy += 14) {
        ctx.fillRect(x + 8, wy, 4, 5);
        ctx.fillRect(x + 21, wy, 4, 5);
      }
    }
    ctx.restore();
  }

  private drawBricks(): void {
    const ctx = this.ctx;
    for (const brick of this.bricks) {
      if (!brick.alive) continue;
      const color = this.brickColor(brick);
      ctx.save();
      ctx.shadowBlur = 13 + brick.flash * 12;
      ctx.shadowColor = color;
      const gradient = ctx.createLinearGradient(brick.x, brick.y, brick.x, brick.y + brick.h);
      gradient.addColorStop(0, this.lighten(color, 0.35));
      gradient.addColorStop(0.45, color);
      gradient.addColorStop(1, this.darken(color, 0.4));
      ctx.fillStyle = gradient;
      this.roundRect(brick.x, brick.y, brick.w, brick.h, 7);
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,.45)';
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.fillStyle = 'rgba(255,255,255,.16)';
      this.roundRect(brick.x + 3, brick.y + 3, brick.w - 6, 6, 3);
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.fillStyle = 'rgba(255,255,255,.92)';
      ctx.font = 'bold 12px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      if (brick.kind === 'armored') ctx.fillText(brick.hp > 1 ? '◆◆' : '◆', brick.x + brick.w / 2, brick.y + brick.h / 2 + 1);
      if (brick.kind === 'explosive') ctx.fillText('✹', brick.x + brick.w / 2, brick.y + brick.h / 2 + 1);
      if (brick.kind === 'bonus') ctx.fillText('★', brick.x + brick.w / 2, brick.y + brick.h / 2 + 1);
      ctx.restore();
    }
  }

  private brickColor(brick: Brick): string {
    if (brick.kind === 'armored') return '#9f54ff';
    if (brick.kind === 'explosive') return '#ff6b22';
    if (brick.kind === 'bonus') return '#35eead';
    const index = Math.floor((brick.y - 72) / 38);
    return ['#27d3ff', '#3f86ff', '#6e62ff', '#b24dff', '#ff5aa5', '#ff9c2c', '#4ed9c2'][index % 7];
  }

  private drawPaddle(): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.shadowColor = '#40dfff';
    ctx.shadowBlur = 18;
    const g = ctx.createLinearGradient(this.paddle.x, this.paddle.y, this.paddle.x, this.paddle.y + this.paddle.h);
    g.addColorStop(0, '#e9fbff');
    g.addColorStop(0.25, '#60c8ff');
    g.addColorStop(0.55, '#162c5a');
    g.addColorStop(1, '#f28a18');
    ctx.fillStyle = g;
    this.roundRect(this.paddle.x, this.paddle.y, this.paddle.w, this.paddle.h, 8);
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,.7)';
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.fillStyle = '#46eeff';
    this.roundRect(this.paddle.x + this.paddle.w * 0.23, this.paddle.y + 5, this.paddle.w * 0.54, 4, 2);
    ctx.fill();
    ctx.restore();
  }

  private drawBalls(): void {
    const ctx = this.ctx;
    for (const ball of this.balls) {
      ball.trail.forEach((point, i) => {
        ctx.beginPath();
        ctx.fillStyle = `rgba(70,218,255,${point.alpha * (1 - i / 11)})`;
        ctx.arc(point.x, point.y, Math.max(1, ball.radius - i * 0.45), 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.save();
      ctx.shadowColor = this.powerLevel > 0 ? '#ff9d2e' : '#4ce7ff';
      ctx.shadowBlur = 22;
      const g = ctx.createRadialGradient(ball.x - 3, ball.y - 3, 1, ball.x, ball.y, ball.radius + 2);
      g.addColorStop(0, '#ffffff');
      g.addColorStop(0.35, this.powerLevel > 0 ? '#ffd26e' : '#72f0ff');
      g.addColorStop(1, this.powerLevel > 0 ? '#ff5f00' : '#126dff');
      ctx.fillStyle = g;
      ctx.beginPath(); ctx.arc(ball.x, ball.y, ball.radius, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
    }
  }

  private drawPowerUps(): void {
    const ctx = this.ctx;
    for (const power of this.powers) {
      ctx.save();
      ctx.translate(power.x, power.y);
      ctx.rotate(power.rotation);
      const color = power.type === 'multi' ? '#a666ff' : power.type === 'wide' ? '#33e3ff' : '#45f0a0';
      ctx.shadowColor = color;
      ctx.shadowBlur = 16;
      ctx.fillStyle = color;
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI * 2 * i) / 6 - Math.PI / 2;
        const px = Math.cos(angle) * 13;
        const py = Math.sin(angle) * 13;
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
      ctx.closePath(); ctx.fill();
      ctx.fillStyle = '#051020';
      ctx.font = 'bold 13px sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(power.type === 'multi' ? '×2' : power.type === 'wide' ? '↔' : '◷', 0, 1);
      ctx.restore();
    }
  }

  private drawParticles(): void {
    const ctx = this.ctx;
    for (const p of this.particles) {
      ctx.globalAlpha = Math.max(0, p.life / p.maxLife);
      ctx.fillStyle = p.color;
      ctx.fillRect(p.x - p.size / 2, p.y - p.size / 2, p.size, p.size);
    }
    ctx.globalAlpha = 1;
  }

  private drawCanvasHud(): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.fillStyle = 'rgba(4,12,34,.78)';
    this.roundRect(18, 14, 182, 42, 12); ctx.fill();
    this.roundRect(WIDTH - 206, 14, 188, 42, 12); ctx.fill();
    ctx.fillStyle = '#8fe9ff';
    ctx.font = '700 11px sans-serif';
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    ctx.fillText('SCORE', 32, 29);
    ctx.fillStyle = '#fff'; ctx.font = '900 20px sans-serif'; ctx.fillText(String(this.score).padStart(6, '0'), 83, 30);
    ctx.fillStyle = '#8fe9ff'; ctx.font = '700 11px sans-serif'; ctx.fillText('LEVEL', WIDTH - 191, 29);
    ctx.fillStyle = '#fff'; ctx.font = '900 18px sans-serif'; ctx.fillText(`${this.levelIndex + 1} · ${this.levelDef.name}`, WIDTH - 143, 30);
    ctx.restore();
  }

  private drawReadyMessage(): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.fillStyle = 'rgba(2,7,23,.72)';
    this.roundRect(WIDTH / 2 - 172, HEIGHT / 2 - 43, 344, 86, 18);
    ctx.fill();
    ctx.strokeStyle = 'rgba(67,218,255,.46)'; ctx.stroke();
    ctx.fillStyle = '#fff'; ctx.font = '900 22px sans-serif'; ctx.textAlign = 'center';
    ctx.fillText('点击画面或按空格发射', WIDTH / 2, HEIGHT / 2 - 5);
    ctx.fillStyle = '#9cc0dc'; ctx.font = '13px sans-serif';
    ctx.fillText('鼠标 / 触摸 / ← → 或 A D 控制挡板', WIDTH / 2, HEIGHT / 2 + 23);
    ctx.restore();
  }

  private roundRect(x: number, y: number, w: number, h: number, r: number): void {
    const ctx = this.ctx;
    const radius = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.arcTo(x + w, y, x + w, y + h, radius);
    ctx.arcTo(x + w, y + h, x, y + h, radius);
    ctx.arcTo(x, y + h, x, y, radius);
    ctx.arcTo(x, y, x + w, y, radius);
    ctx.closePath();
  }

  private lighten(hex: string, amount: number): string { return this.adjustColor(hex, amount); }
  private darken(hex: string, amount: number): string { return this.adjustColor(hex, -amount); }
  private adjustColor(hex: string, amount: number): string {
    const value = hex.replace('#', '');
    const num = parseInt(value, 16);
    const r = Math.max(0, Math.min(255, (num >> 16) + 255 * amount));
    const g = Math.max(0, Math.min(255, ((num >> 8) & 255) + 255 * amount));
    const b = Math.max(0, Math.min(255, (num & 255) + 255 * amount));
    return `rgb(${Math.round(r)},${Math.round(g)},${Math.round(b)})`;
  }

  private snapshot(): GameSnapshot {
    return { score: this.score, level: this.levelIndex + 1, lives: this.lives, combo: this.combo, balls: this.balls.length, status: this.status, highScore: this.highScore };
  }

  private emitSnapshot(force = false): void {
    const snapshot = this.snapshot();
    const serialized = `${snapshot.score}|${snapshot.level}|${snapshot.lives}|${snapshot.combo}|${snapshot.balls}|${snapshot.status}|${snapshot.highScore}`;
    if (force || serialized !== this.lastSnapshot) {
      this.lastSnapshot = serialized;
      this.callbacks.onSnapshot(snapshot);
    }
  }

  private ensureAudio(): void {
    if (!this.soundEnabled || this.audioContext) return;
    try { this.audioContext = new AudioContext(); } catch { this.audioContext = null; }
  }

  private beep(frequency: number, duration: number, volume: number): void {
    if (!this.soundEnabled) return;
    this.ensureAudio();
    const audio = this.audioContext;
    if (!audio) return;
    const oscillator = audio.createOscillator();
    const gain = audio.createGain();
    oscillator.type = 'triangle';
    oscillator.frequency.value = frequency;
    gain.gain.setValueAtTime(volume, audio.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, audio.currentTime + duration);
    oscillator.connect(gain); gain.connect(audio.destination);
    oscillator.start(); oscillator.stop(audio.currentTime + duration);
  }

  private wallSound(): void { this.beep(180, 0.016, 0.012); }

  destroy(): void {
    this.destroyed = true;
    cancelAnimationFrame(this.animationId);
    window.removeEventListener('resize', this.resizeCanvas);
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    this.canvas.removeEventListener('contextmenu', this.preventContextMenu);
    this.audioContext?.close().catch(() => undefined);
  }
}
